import React, { useState } from 'react';

import Page from '@/components/Page';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Heading } from '@/components/Typography';
import Button from '@/components/Button';
import { axiosRubyClient } from '@/util/axios/axiosClient';

const PASSWORD = 'gravydevs123';

type ResetSuccess = {
  user_id: number;
  worker_id: number | null;
  email: string;
};

const PasswordReset: React.FC = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [identifier, setIdentifier] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResetSuccess | null>(null);
  const [error, setError] = useState('');

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === PASSWORD) {
      setAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);

    if (!identifier.trim()) {
      setError('Please enter a worker ID or email');
      return;
    }
    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosRubyClient.post('/api/admin/password_reset', {
        identifier: identifier.trim(),
        new_password: newPassword,
      });
      setResult(response.data.data);
      setIdentifier('');
      setNewPassword('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!authenticated) {
    return (
      <Page size="full" title="Password Reset (Dev)">
        <Card>
          <Card.Section>
            <form onSubmit={handlePasswordSubmit}>
              <Stack gap={16} vertical>
                <Heading>Enter Password</Heading>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  style={{
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    width: '300px',
                  }}
                />
                {passwordError && (
                  <Body style={{ color: 'red' }}>{passwordError}</Body>
                )}
                <Button type="submit" label="Submit" />
              </Stack>
            </form>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  return (
    <Page size="full" title="Password Reset (Dev)">
      <Card>
        <Card.Section>
          <form onSubmit={handleSubmit}>
            <Stack gap={16} vertical>
              <Heading>Reset User Password</Heading>
              <Body>
                Enter the worker ID (numeric) or the user's email, then set a
                new password. Password complexity rules are bypassed.
              </Body>

              <label
                style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
              >
                <Body style={{ fontWeight: 600 }}>Worker ID or Email</Body>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="20920 or worker@example.com"
                  style={{
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    width: '100%',
                    maxWidth: 400,
                  }}
                />
              </label>

              <label
                style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
              >
                <Body style={{ fontWeight: 600 }}>New Password</Body>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password"
                  autoComplete="off"
                  style={{
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    width: '100%',
                    maxWidth: 400,
                    fontFamily: 'monospace',
                  }}
                />
              </label>

              {error && <Body style={{ color: 'red' }}>{error}</Body>}

              <Button
                type="submit"
                label={loading ? 'Saving...' : 'Save'}
                disabled={loading}
              />
            </Stack>
          </form>
        </Card.Section>
      </Card>

      {result && (
        <Card style={{ marginTop: '16px' }}>
          <Card.Section>
            <Stack gap={8} vertical>
              <Heading>Password updated</Heading>
              <Body style={{ color: 'green', fontWeight: 600 }}>Success</Body>
              <Body>user_id: {result.user_id}</Body>
              <Body>worker_id: {result.worker_id ?? '—'}</Body>
              <Body>email: {result.email}</Body>
            </Stack>
          </Card.Section>
        </Card>
      )}
    </Page>
  );
};

export default PasswordReset;
